import { publicAuthErrorCodeFromError } from "@/lib/auth/auth-errors";

/**
 * Terminal handling for session-context failures surfaced through react-query.
 *
 * When the authz.session_tenant_context row expires underneath a still-live
 * Supabase auth session (30-day cap, no sliding renewal), every server action
 * throws SessionContextError("no_workspace_context"). Retrying or refetching
 * can never succeed — only a fresh sign-in fixes it. Without terminal
 * handling the inbox client refetch-hammered a dead session: 72 Sentry events
 * in 63 seconds from a single user.
 *
 * Detection rides on `Error.digest`: production Next.js masks thrown server
 * action messages but forwards a pre-set digest verbatim, so the
 * SessionContextError code survives the wire (see SESSION_CONTEXT_ERROR_
 * DIGEST_PREFIX in lib/auth/auth-errors.ts).
 */

/**
 * The `/login?error=…` path this failure should terminate at, or null when
 * the error is not a terminal session-context failure. Only failure codes
 * with a public login-error mapping are terminal — transient codes
 * (write_failed, refresh_failed, …) stay retryable.
 */
export function loginPathForTerminalSessionError(error: unknown): string | null {
  const loginError = publicAuthErrorCodeFromError(error);
  return loginError ? `/login?error=${loginError}` : null;
}

export function isTerminalSessionContextError(error: unknown): boolean {
  return loginPathForTerminalSessionError(error) !== null;
}

let redirectInFlight = false;

/** Test-only: reset the once-per-page redirect guard. */
export function resetTerminalSessionRedirectForTests(): void {
  redirectInFlight = false;
}

/**
 * If the error is a terminal session-context failure, hard-navigate to the
 * login page (which clears the local session for these error codes — see
 * AUTH_ERRORS_THAT_CLEAR_LOCAL_SESSION) and report that the error was
 * terminal. Idempotent: concurrent query failures trigger a single redirect.
 * A full navigation (not router.push) is intentional — it tears down the
 * query cache and any pending refetch loops with it.
 */
export function redirectIfTerminalSessionError(error: unknown): boolean {
  const path = loginPathForTerminalSessionError(error);
  if (!path) return false;
  if (typeof window === "undefined") return true;
  if (redirectInFlight) return true;
  redirectInFlight = true;
  window.location.assign(path);
  return true;
}
